import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Check, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { PublicNav } from "@/components/PublicNav";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { formatEuroCents } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";

export function PricingCards({ compact = false }) {
  const { t } = useT();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);

  const { data } = useQuery({ queryKey: ["config"], queryFn: async () => (await api.get("/config")).data });
  const plans = data?.plans || [];

  const subscribe = async (plan) => {
    if (!user) { navigate("/register"); return; }
    if (plan.id === "free") { navigate("/dashboard"); return; }
    setBusy(plan.id);
    try {
      const res = await api.post("/payments/checkout", { lookup_key: plan.lookup_key, origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || t("errors.generic"));
      setBusy(null);
    }
  };

  const featureKey = { free: "pricing.free.features", pro: "pricing.pro.features", agency: "pricing.agency.features" };
  const ctaKey = { free: "pricing.free.cta", pro: "pricing.pro.cta", agency: "pricing.agency.cta" };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      {plans.map((plan, i) => {
        const features = t(featureKey[plan.id]) || [];
        const highlight = plan.highlight;
        const isCurrent = user?.plan === plan.id;
        return (
          <motion.div key={plan.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
            className={`relative flex flex-col rounded-3xl border p-7 ${highlight ? "border-brand-500 bg-card shadow-xl shadow-brand-500/10 lg:scale-[1.03]" : "border-border bg-card shadow-sm"}`}
            data-testid={`plan-${plan.id}`}>
            {highlight && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-brand-500 to-brand-700 px-4 py-1 text-xs font-bold uppercase tracking-wider text-white shadow-lg">
                {t("pricing.mostChosen")}
              </div>
            )}
            <h3 className="font-display text-xl font-bold">{plan.name}</h3>
            <div className="mt-4 flex items-end gap-1">
              <span className="font-display text-4xl font-bold">{plan.price_cents === 0 ? "€0" : formatEuroCents(plan.price_cents)}</span>
              {plan.price_cents > 0 && <span className="mb-1 text-muted-foreground">{t("pricing.perMonth")}</span>}
            </div>
            <ul className="mt-6 flex-1 space-y-3">
              {(Array.isArray(features) ? features : []).map((f, j) => (
                <li key={j} className="flex items-start gap-2.5 text-sm">
                  <Check className={`mt-0.5 h-4 w-4 shrink-0 ${highlight ? "text-brand-500" : "text-emerald-500"}`} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <Button onClick={() => subscribe(plan)} disabled={busy === plan.id || isCurrent} data-testid={`subscribe-${plan.id}`}
              className={`mt-7 h-11 rounded-xl ${highlight ? "bg-brand-500 hover:bg-brand-600 text-black" : ""}`}
              variant={highlight ? "default" : "outline"}>
              {isCurrent ? t("pricing.currentPlan") : busy === plan.id ? t("common.loading") : t(ctaKey[plan.id])}
            </Button>
          </motion.div>
        );
      })}
    </div>
  );
}

export function Pricing() {
  const { t } = useT();
  return (
    <div className="min-h-screen bg-background">
      <PublicNav />
      <div className="gp-hero-gradient">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <div className="mx-auto max-w-2xl text-center">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground">
              <Sparkles className="h-3.5 w-3.5 text-brand-500" /> {t("pricing.subtitle")}
            </div>
            <h1 className="font-display text-4xl font-bold tracking-tight sm:text-5xl">{t("pricing.title")}</h1>
          </div>
          <div className="mt-14"><PricingCards /></div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
